import React from "react";
import { Image, FileText, Award } from "lucide-react";
import DocumentCard from "../documents/DocumentCard";

const isHtml = (str) => typeof str === "string" && /<[a-z][\s\S]*>/i.test(str);

const ActivityContent = ({ activity, onImageClick, onDocView }) => {
  return (
    <div className="space-y-3 text-xs text-slate-700">
      {/* Activity Description / Content */}
      {activity.content && (
        <div className="bg-slate-50 p-3 rounded-xl border border-slate-200/80 leading-relaxed font-normal">
          {isHtml(activity.content) ? (
            <div
              className="prose prose-xs max-w-none text-slate-700 [&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4"
              dangerouslySetInnerHTML={{ __html: activity.content }}
            />
          ) : (
            <div className="whitespace-pre-wrap">{activity.content}</div>
          )}
        </div>
      )}

      {/* Categories & Procedures */}
      {(activity.procedure_category || activity.procedure_sub_type || activity.competency_address) && (
        <div className="flex flex-wrap gap-2 text-[11px] font-semibold text-slate-600">
          {activity.procedure_category && (
            <span className="bg-slate-100 border border-slate-200 px-2 py-0.5 rounded">
              Category: {activity.procedure_category}
            </span>
          )}
          {activity.procedure_sub_type && (
            <span className="bg-slate-100 border border-slate-200 px-2 py-0.5 rounded">
              Sub-type: {activity.procedure_sub_type}
            </span>
          )}
          {activity.competency_address && (
            <span className="bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5 rounded">
              Competency Code: {activity.competency_address}
            </span>
          )}
        </div>
      )}

      {/* MBBS Competencies List (if present) */}
      {activity.competencies && activity.competencies.length > 0 && (
        <div className="bg-indigo-50/70 border border-indigo-100 rounded-xl p-3">
          <h4 className="font-bold text-indigo-900 mb-1.5 flex items-center gap-1.5 text-xs">
            <Award className="w-3.5 h-3.5 text-indigo-600" />
            MBBS Competencies Addressed ({activity.competencies.length})
          </h4>
          <ul className="space-y-1 pl-1">
            {activity.competencies.map((comp, idx) => (
              <li key={idx} className="text-[11px] text-indigo-800 font-medium leading-normal">
                <span className="font-bold text-indigo-950 mr-1">{comp.code || comp.competency_code}:</span>
                {comp.description || comp.details || comp.name}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Base64 Drawing Canvas */}
      {activity.drawing && (
        <div className="mt-2">
          <h4 className="font-semibold text-slate-500 mb-1 text-[11px]">Clinical Drawing Canvas:</h4>
          <div className="border border-slate-200 rounded-xl p-2 bg-white flex justify-center max-h-48 overflow-hidden">
            <img
              src={activity.drawing}
              alt="Clinical Drawing"
              className="max-h-44 object-contain cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => onImageClick && onImageClick(activity.drawing)}
            />
          </div>
        </div>
      )}

      {/* Attached Images */}
      {activity.images && activity.images.length > 0 && (
        <div className="mt-2">
          <h4 className="font-semibold text-slate-500 mb-1.5 flex items-center gap-1 text-[11px]">
            <Image className="w-3.5 h-3.5 text-blue-500" /> Attached Images ({activity.images.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {activity.images.map((img, idx) => {
              const src = typeof img === "string" ? img : img.url || img.data;
              return (
                <img
                  key={idx}
                  src={src}
                  alt={`Attachment ${idx + 1}`}
                  className="w-16 h-16 object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => onImageClick && onImageClick(src)}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Attached Documents */}
      {activity.documents && activity.documents.length > 0 && (
        <div className="mt-2">
          <h4 className="font-semibold text-slate-500 mb-1.5 flex items-center gap-1 text-[11px]">
            <FileText className="w-3.5 h-3.5 text-blue-500" /> Attached Documents ({activity.documents.length})
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {activity.documents.map((doc, idx) => (
              <DocumentCard key={idx} doc={doc} onView={onDocView} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityContent;
